/**
 * Offices Section
 * Использует переиспользуемые компоненты
 */
import React, { useEffect, useRef } from 'react';
import { Text, Icon, Container } from '../components';
import { THEME_PRESETS } from '../../lib/themes';

const WIDGET_BASE_URL = 'https://arendavmeste.ru/widget';

// Загрузка скрипта в указанный document (для поддержки iframe)
const loadedScriptsPerDoc = new WeakMap();
function loadScript(src, targetDoc = document) {
  let loadedScripts = loadedScriptsPerDoc.get(targetDoc);
  if (!loadedScripts) {
    loadedScripts = new Set();
    loadedScriptsPerDoc.set(targetDoc, loadedScripts);
  }

  if (loadedScripts.has(src)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = targetDoc.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      loadedScripts.add(src);
      resolve();
    };
    script.onerror = reject;
    targetDoc.body.appendChild(script);
  });
}

// Конвертировать CSS значение в пиксели
function parseRadius(value) {
  if (!value) return 12;
  const num = parseFloat(value);
  if (value.includes('rem')) {
    return Math.round(num * 16); // 1rem = 16px
  }
  return Math.round(num) || 12;
}

// Получить тему для виджета
function getWidgetTheme(themeKey) {
  const theme = THEME_PRESETS[themeKey] || THEME_PRESETS.default;
  return {
    primaryColor: theme.colors.primary,
    accentColor: theme.colors.success,
    borderRadius: parseRadius(theme.radius.md),
    fontFamily: theme.typography.fontFamily,
  };
}

// Компонент виджета офиса
function OfficeWidget({ officeId, cardStyle, themeKey }) {
  const containerRef = useRef(null);
  const widgetInitialized = useRef(false);

  useEffect(() => {
    if (!officeId || !containerRef.current || widgetInitialized.current) return;

    // Получаем document и window контейнера (может быть iframe)
    const targetDoc = containerRef.current.ownerDocument;
    const targetWin = targetDoc.defaultView;

    const widgetFile = cardStyle === 'detailed' ? 'office.js' : 'card.js';
    const WidgetClass = cardStyle === 'detailed' ? 'OfficeWidget' : 'OfficeCard';

    async function initWidget() {
      try {
        // Загружаем скрипты в правильный document
        // booking.js нужен для работы кнопки "Забронировать" в карточке
        await loadScript(`${WIDGET_BASE_URL}/booking.js`, targetDoc);
        await loadScript(`${WIDGET_BASE_URL}/${widgetFile}`, targetDoc);

        const widgetTheme = getWidgetTheme(themeKey);

        // Инициализируем виджет карточки из правильного window
        if (targetWin[WidgetClass] && containerRef.current) {
          targetWin[WidgetClass].init({
            officeId: officeId,
            container: containerRef.current,
            theme: widgetTheme
          });
          widgetInitialized.current = true;
        }
      } catch (error) {
        console.error('Failed to load widget:', error);
      }
    }

    initWidget();

    // Cleanup при unmount
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      widgetInitialized.current = false;
    };
  }, [officeId, cardStyle, themeKey]);

  return (
    <div ref={containerRef} />
  );
}

export default function OfficesSection({ data, styles, theme = 'default' }) {
  const offices = data?.offices || [];
  const mode = data?.mode || 'list';
  const cardStyle = data?.cardStyle || 'compact';
  const columns = data?.columns || 2;

  const getGridClasses = () => {
    if (mode === 'single') return '';
    switch (columns) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-1 md:grid-cols-2';
      case 3: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      default: return 'grid-cols-1 md:grid-cols-2';
    }
  };

  const officesToRender = mode === 'single' ? offices.slice(0, 1) : offices;

  return (
    <section
      style={{
        paddingTop: styles?.paddingTop || 64,
        paddingBottom: styles?.paddingBottom || 64,
        backgroundColor: styles?.backgroundColor || '#FFFFFF'
      }}
    >
      <Container>
        {/* Заголовок */}
        <Text
          as="h2"
          data={typeof data?.title === 'object' ? data.title : { text: data?.title }}
          defaults={{ size: '3xl', weight: 'bold', color: 'foreground' }}
          align="center"
          className={data?.subtitle ? 'mb-2 md:mb-3' : 'mb-8 md:mb-12'}
        />

        {/* Подзаголовок */}
        <Text
          as="p"
          data={typeof data?.subtitle === 'object' ? data.subtitle : { text: data?.subtitle }}
          defaults={{ size: 'lg', weight: 'normal', color: 'text-secondary' }}
          align="center"
          className="mb-8 md:mb-12"
        />

        {offices.length > 0 ? (
          <div className={mode === 'list' ? `grid gap-6 ${getGridClasses()}` : 'max-w-2xl mx-auto'}>
            {officesToRender.map((office, index) => (
              office.officeId ? (
                <OfficeWidget
                  key={office.id || index}
                  officeId={office.officeId}
                  cardStyle={cardStyle}
                  themeKey={theme}
                />
              ) : null
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-text-secondary">
            <Icon name="Building2" size="3xl" className="mx-auto mb-4 opacity-50" />
            <p>Добавьте кабинеты в настройках</p>
          </div>
        )}
      </Container>
    </section>
  );
}
